import axios from 'axios'
import {
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  ChangePasswordRequest,
  ResetPasswordRequest,
  SetupPasswordRequest,
  VerifyResetTokenResponse,
  User,
} from '@/types/user'

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api'

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          })
          
          const { accessToken, refreshToken: newRefreshToken } = response.data
          localStorage.setItem('accessToken', accessToken)
          localStorage.setItem('refreshToken', newRefreshToken)
          
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

export const authService = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post('/auth/login', credentials)
    return response.data
  },

  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await apiClient.post('/auth/refresh', { refreshToken })
    return response.data
  },

  async logout(): Promise<void> {
    await apiClient.post('/auth/logout')
  },

  async getProfile(): Promise<User> {
    const response = await apiClient.get('/auth/profile')
    return response.data.user
  },

  async changePassword(data: ChangePasswordRequest): Promise<void> {
    await apiClient.post('/auth/change-password', data)
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await apiClient.post('/auth/reset-password', { email })
    return response.data
  },

  async verifyResetToken(token: string): Promise<VerifyResetTokenResponse> {
    const response = await apiClient.post('/auth/verify-reset-token', { token })
    return response.data
  },

  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    await apiClient.post('/auth/reset-password', data)
  },

  async setupPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await apiClient.post('/auth/setup-password', { token, newPassword })
    return response.data
  },

  async getUserPermissions(userId: number): Promise<string[]> {
    const response = await apiClient.get(`/permissions/user/${userId}`)
    // Return an array of permission names
    return response.data.permissions.map((p: any) => p.name)
  },

  async getMyPermissions(): Promise<string[]> {
    const response = await apiClient.get('/permissions/my-permissions')
    // Return an array of permission names
    return response.data.permissions.map((p: any) => p.name)
  },
} 